import React, { useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  Visibility,
  People,
  Schedule,
  AttachMoney,
  School,
  Work,
  PersonAdd,
  Assessment,
  CalendarToday,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useStore } from "../../store/useStore";
import { useNotification } from "../../components/common/NotificationProvider";
import { useFormValidation, commonValidationRules } from "../../hooks/useFormValidation";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`hr-tabpanel-${index}`}
      aria-labelledby={`hr-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const HR: React.FC = () => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useNotification();
  const { employees, addEmployee, updateEmployee, deleteEmployee } = useStore();
  const [tabValue, setTabValue] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState("");
  const [editingEmployee, setEditingEmployee] = useState<any>(null);
  const [formData, setFormData] = useState({
    name: "",
    position: "",
    department: "",
    salary: "",
    joinDate: "",
    status: "Active",
  });

  const validationRules = {
    name: commonValidationRules.name,
    position: { required: true, minLength: 2 },
    department: { required: true },
    salary: commonValidationRules.amount,
    joinDate: commonValidationRules.date,
  };

  const { errors, validateForm, validateFieldOnChange, clearErrors } = useFormValidation(validationRules);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleOpenDialog = (type: string, employee?: any) => {
    setDialogType(type);
    if (employee) {
      setEditingEmployee(employee);
      setFormData({
        name: employee.name,
        position: employee.position,
        department: employee.department,
        salary: employee.salary.toString(),
        joinDate: employee.joinDate,
        status: employee.status,
      });
    } else {
      setEditingEmployee(null);
      setFormData({
        name: "",
        position: "",
        department: "",
        salary: "",
        joinDate: "",
        status: "Active",
      });
    }
    clearErrors();
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setDialogType("");
    setEditingEmployee(null);
    setFormData({
      name: "",
      position: "",
      department: "",
      salary: "",
      joinDate: "",
      status: "Active",
    });
    clearErrors();
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    validateFieldOnChange(field, value);
  };

  const handleSubmit = () => {
    if (!validateForm(formData)) {
      showError("Please fix the form errors before submitting");
      return;
    }

    try {
      const employeeData = {
        name: formData.name,
        position: formData.position,
        department: formData.department,
        salary: parseFloat(formData.salary),
        joinDate: formData.joinDate,
        status: formData.status,
      };

      if (editingEmployee) {
        updateEmployee(editingEmployee.id, employeeData);
        showSuccess("Employee updated successfully");
      } else {
        addEmployee(employeeData);
        showSuccess("Employee added successfully");
      }
      handleCloseDialog();
    } catch (error) {
      showError("Failed to save employee");
    }
  };

  const handleDelete = (id: number) => {
    if (window.confirm("Are you sure you want to delete this employee?")) {
      try {
        deleteEmployee(id);
        showSuccess("Employee deleted successfully");
      } catch (error) {
        showError("Failed to delete employee");
      }
    }
  };

  // Use data from store

  const attendanceData = [
    {
      date: "2024-01-15",
      employee: "Dr. Sarah Al-Rashid",
      checkIn: "08:00",
      checkOut: "17:00",
      status: "Present",
    },
    {
      date: "2024-01-15",
      employee: "Ahmed Hassan",
      checkIn: "09:00",
      checkOut: "18:00",
      status: "Present",
    },
    {
      date: "2024-01-15",
      employee: "Nurse Fatima Al-Zahra",
      checkIn: "07:30",
      checkOut: "19:30",
      status: "Overtime",
    },
    {
      date: "2024-01-15",
      employee: "Dr. Mohammed Al-Sayed",
      checkIn: "-",
      checkOut: "-",
      status: "Absent",
    },
  ];

  const payrollData = [
    {
      employee: "Dr. Sarah Al-Rashid",
      basicSalary: 25000,
      allowances: 3000,
      deductions: 500,
      netSalary: 27500,
      status: "Paid",
    },
    {
      employee: "Ahmed Hassan",
      basicSalary: 18000,
      allowances: 2000,
      deductions: 300,
      netSalary: 19700,
      status: "Paid",
    },
    {
      employee: "Nurse Fatima Al-Zahra",
      basicSalary: 12000,
      allowances: 1500,
      deductions: 200,
      netSalary: 13300,
      status: "Pending",
    },
    {
      employee: "Dr. Mohammed Al-Sayed",
      basicSalary: 22000,
      allowances: 2500,
      deductions: 400,
      netSalary: 24100,
      status: "Paid",
    },
  ];

  const recruitmentData = [
    {
      position: "Senior Cardiologist",
      department: "Cardiology",
      applicants: 12,
      status: "Interviewing",
      postedDate: "2024-01-10",
    },
    {
      position: "IT Support Specialist",
      department: "IT",
      applicants: 8,
      status: "Screening",
      postedDate: "2024-01-12",
    },
    {
      position: "Nurse Manager",
      department: "Nursing",
      applicants: 15,
      status: "Interviewing",
      postedDate: "2024-01-08",
    },
    {
      position: "Finance Analyst",
      department: "Finance",
      applicants: 6,
      status: "Offered",
      postedDate: "2024-01-05",
    },
  ];

  const trainingData = [
    {
      course: "HIPAA Compliance Training",
      participants: 45,
      completion: 38,
      status: "In Progress",
    },
    {
      course: "Advanced Cardiac Life Support",
      participants: 20,
      completion: 20,
      status: "Completed",
    },
    {
      course: "Healthcare IT Security",
      participants: 30,
      completion: 25,
      status: "In Progress",
    },
    {
      course: "Patient Safety Protocols",
      participants: 60,
      completion: 60,
      status: "Completed",
    },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Active":
      case "Present":
      case "Paid":
      case "Completed":
        return "success";
      case "On Leave":
      case "Overtime":
      case "Pending":
      case "In Progress":
        return "warning";
      case "Absent":
        return "error";
      default:
        return "default";
    }
  };

  const upcomingEvents = [
    {
      title: "Monthly Staff Meeting",
      date: "2024-01-20",
      time: "10:00 AM",
      type: "Meeting",
    },
    {
      title: "HIPAA Training Session",
      date: "2024-01-22",
      time: "02:00 PM",
      type: "Training",
    },
    {
      title: "Performance Review Period",
      date: "2024-01-25",
      time: "All Day",
      type: "Review",
    },
    {
      title: "New Employee Orientation",
      date: "2024-01-28",
      time: "09:00 AM",
      type: "Orientation",
    },
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {t("hr.title")}
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        {t("hr.description")}
      </Typography>

      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 3, width: "100%" }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <People color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">456</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Employees
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Schedule color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">92%</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Attendance Rate
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <AttachMoney color="warning" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">SAR 2.1M</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Monthly Payroll
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <School color="info" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">15</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Trainings
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label={t("hr.employeeDatabase")} />
            <Tab label={t("hr.attendance")} />
            <Tab label={t("hr.payroll")} />
            <Tab label={t("hr.recruitment")} />
            <Tab label={t("hr.training")} />
            <Tab label={t("hr.performance")} />
          </Tabs>
        </Box>

        {/* Employee Database Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">Employee Database</Typography>
            <Button
              variant="contained"
              startIcon={<PersonAdd />}
              onClick={() => handleOpenDialog("employee")}
            >
              Add Employee
            </Button>
          </Box>
          <TableContainer component={Paper} sx={{ width: "100%" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell>Position</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Salary</TableCell>
                  <TableCell>Join Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {employees.map((employee) => (
                  <TableRow key={employee.id}>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Avatar sx={{ mr: 2, width: 32, height: 32 }}>
                          {employee.name
                            .split(" ")
                            .map((n) => n[0])
                            .join("")}
                        </Avatar>
                        {employee.name}
                      </Box>
                    </TableCell>
                    <TableCell>{employee.position}</TableCell>
                    <TableCell>{employee.department}</TableCell>
                    <TableCell>
                      <Chip
                        label={employee.status}
                        color={getStatusColor(employee.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      SAR {employee.salary.toLocaleString()}
                    </TableCell>
                    <TableCell>{employee.joinDate}</TableCell>
                    <TableCell>
                      <IconButton 
                        size="small"
                        onClick={() => handleOpenDialog("employee", employee)}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton 
                        size="small"
                        onClick={() => handleDelete(employee.id)}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Attendance Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">Attendance Management</Typography>
            <Button
              variant="contained"
              startIcon={<CalendarToday />}
              onClick={() => handleOpenDialog("attendance")}
            >
              Mark Attendance
            </Button>
          </Box>
          <TableContainer component={Paper} sx={{ width: "100%" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Employee</TableCell>
                  <TableCell>Check In</TableCell>
                  <TableCell>Check Out</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {attendanceData.map((record, index) => (
                  <TableRow key={index}>
                    <TableCell>{record.date}</TableCell>
                    <TableCell>{record.employee}</TableCell>
                    <TableCell>{record.checkIn}</TableCell>
                    <TableCell>{record.checkOut}</TableCell>
                    <TableCell>
                      <Chip
                        label={record.status}
                        color={getStatusColor(record.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small">
                        <Edit />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Payroll Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">Payroll Management</Typography>
            <Button
              variant="contained"
              startIcon={<AttachMoney />}
              onClick={() => handleOpenDialog("payroll")}
            >
              Process Payroll
            </Button>
          </Box>
          <TableContainer component={Paper} sx={{ width: "100%" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell align="right">Basic Salary</TableCell>
                  <TableCell align="right">Allowances</TableCell>
                  <TableCell align="right">Deductions</TableCell>
                  <TableCell align="right">Net Salary</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payrollData.map((payroll, index) => (
                  <TableRow key={index}>
                    <TableCell>{payroll.employee}</TableCell>
                    <TableCell align="right">
                      SAR {payroll.basicSalary.toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      SAR {payroll.allowances.toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      SAR {payroll.deductions.toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      SAR {payroll.netSalary.toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={payroll.status}
                        color={getStatusColor(payroll.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small">
                        <Visibility />
                      </IconButton>
                      <IconButton size="small">
                        <Edit />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Recruitment Tab */}
        <TabPanel value={tabValue} index={3}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">Recruitment</Typography>
            <Button
              variant="contained"
              startIcon={<Work />}
              onClick={() => handleOpenDialog("recruitment")}
            >
              Post Job
            </Button>
          </Box>
          <TableContainer component={Paper} sx={{ width: "100%" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Position</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell align="right">Applicants</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Posted Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recruitmentData.map((job, index) => (
                  <TableRow key={index}>
                    <TableCell>{job.position}</TableCell>
                    <TableCell>{job.department}</TableCell>
                    <TableCell align="right">{job.applicants}</TableCell>
                    <TableCell>
                      <Chip
                        label={job.status}
                        color={getStatusColor(job.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{job.postedDate}</TableCell>
                    <TableCell>
                      <IconButton size="small">
                        <Visibility />
                      </IconButton>
                      <IconButton size="small">
                        <Edit />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Training Tab */}
        <TabPanel value={tabValue} index={4}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">Training & Development</Typography>
            <Button
              variant="contained"
              startIcon={<School />}
              onClick={() => handleOpenDialog("training")}
            >
              Schedule Training
            </Button>
          </Box>
          <TableContainer component={Paper} sx={{ width: "100%" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Course</TableCell>
                  <TableCell align="right">Participants</TableCell>
                  <TableCell align="right">Completed</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {trainingData.map((training, index) => (
                  <TableRow key={index}>
                    <TableCell>{training.course}</TableCell>
                    <TableCell align="right">{training.participants}</TableCell>
                    <TableCell align="right">{training.completion}</TableCell>
                    <TableCell>
                      <Chip
                        label={training.status}
                        color={getStatusColor(training.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small">
                        <Visibility />
                      </IconButton>
                      <IconButton size="small">
                        <Edit />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Performance Tab */}
        <TabPanel value={tabValue} index={5}>
          <Typography variant="h6" gutterBottom>
            Performance Management
          </Typography>
          <Grid container spacing={2} sx={{ width: "100%" }}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Upcoming Reviews
                  </Typography>
                  <List>
                    {upcomingEvents.map((event, index) => (
                      <ListItem
                        key={index}
                        divider={index < upcomingEvents.length - 1}
                      >
                        <ListItemAvatar>
                          <Avatar>
                            <Assessment />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={event.title}
                          secondary={`${event.date} at ${event.time}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Performance Metrics
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" gutterBottom>
                      Average Performance Rating: 4.2/5
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      Employees Exceeding Goals: 78%
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      Training Completion Rate: 92%
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      Employee Satisfaction: 4.5/5
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Card>

      {/* Dialog for adding new items */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {dialogType === "employee" && "Add New Employee"}
          {dialogType === "attendance" && "Mark Attendance"}
          {dialogType === "payroll" && "Process Payroll"}
          {dialogType === "recruitment" && "Post New Job"}
          {dialogType === "training" && "Schedule Training"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Name"
              variant="outlined"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              error={!!errors.name}
              helperText={errors.name}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Position"
              variant="outlined"
              value={formData.position}
              onChange={(e) => handleInputChange("position", e.target.value)}
              error={!!errors.position}
              helperText={errors.position}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }} error={!!errors.department}>
              <InputLabel>Department</InputLabel>
              <Select 
                label="Department"
                value={formData.department}
                onChange={(e) => handleInputChange("department", e.target.value)}
              >
                <MenuItem value="Medical">Medical</MenuItem>
                <MenuItem value="Nursing">Nursing</MenuItem>
                <MenuItem value="Human Resources">Human Resources</MenuItem>
                <MenuItem value="Finance">Finance</MenuItem>
                <MenuItem value="IT">IT</MenuItem>
                <MenuItem value="Cardiology">Cardiology</MenuItem>
                <MenuItem value="Emergency">Emergency</MenuItem>
              </Select>
              {errors.department && (
                <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                  {errors.department}
                </Typography>
              )}
            </FormControl>
            <TextField
              fullWidth
              label="Salary"
              type="number"
              variant="outlined"
              value={formData.salary}
              onChange={(e) => handleInputChange("salary", e.target.value)}
              error={!!errors.salary}
              helperText={errors.salary}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Join Date"
              type="date"
              variant="outlined"
              value={formData.joinDate}
              onChange={(e) => handleInputChange("joinDate", e.target.value)}
              error={!!errors.joinDate}
              helperText={errors.joinDate}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Status</InputLabel>
              <Select 
                label="Status"
                value={formData.status}
                onChange={(e) => handleInputChange("status", e.target.value)}
              >
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="On Leave">On Leave</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>
            {editingEmployee ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HR;
